import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../../firebase/config.js'; 
import { collection, query, doc, setDoc, where, limit, onSnapshot, serverTimestamp, getDocs } from 'firebase/firestore';

// IMPORTAMOS LA LÓGICA CENTRALIZADA Y LA LIMPIEZA DESDE FIREBASEUTILS
import { deletePolizaFull, rehabilitarPolizaFull } from '../../../../firebase/firebaseUtils';

// Componentes Comunes
import CardPoliza from '../../../ui/CardPoliza';
import AuthModal from '../../../ui/AuthModal';
import ModalEmision from '../../../ui/ModalEmision';
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza';
import ModalAnulacion from '../../../ui/ModalAnulacion';

/**
 * Helper síncrono y ultra-seguro para calcular el estado real en memoria basándose en las fechas.
 * Mapea el 100% de la lógica de CardPoliza para garantizar consistencia total (disparando actualización si el inicio es nulo).
 */
const getPolizaStatusInRealtime = (p) => {
    if (!p) return 'PENDIENTE';
    const estadoOriginal = (p.estadoEmision || '').toUpperCase();
    
    // Si está anulada manualmente por el operador, respetamos ese estado comercial
    if (estadoOriginal === 'ANULADA') return 'ANULADA';

    // No recalculamos por fecha si el estado es pendiente o propuesta ingresada
    const noCalcular = [
        'PENDIENTE DE EMISIÓN',
        'PENDIENTE DE INSPECCIÓN',
        'PROPUESTA INGRESADA'
    ].includes(estadoOriginal);

    if (noCalcular) return estadoOriginal;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = p.inicioVigencia ? new Date(p.inicioVigencia + "T00:00:00") : null;
    const end = p.finVigencia ? new Date(p.finVigencia + "T00:00:00") : null;

    // 1. Verificación de Vencimiento: Si la fecha fin ya pasó, la póliza está VENCIDA (independientemente del inicio)
    if (end && today > end) return 'VENCIDA';

    // 2. Verificación de Inicio: Si la fecha de inicio es futura, está NO INICIADA
    if (start && today < start) return 'NO INICIADA';

    // 3. Si tiene al menos una fecha activa y pasó las anteriores, está VIGENTE
    if (start || end) return 'VIGENTE';

    return estadoOriginal || 'PENDIENTE';
};

const PolizasTab = ({ client }) => {
  const navigate = useNavigate();
  const [polizas, setPolizas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  
  const [refactMap, setRefactMap] = useState({});
  
  const [filters, setFilters] = useState({
    poliza: '',
    patente: '',
    estado: 'VIGENTE', 
    motivo: '',
    riesgo: '',
    compania: ''
  });

  const [showEmissionModal, setShowEmissionModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAnulacionModal, setShowAnulacionModal] = useState(false);
  
  const [editingPoliza, setEditingPoliza] = useState(null);
  const [viewingPoliza, setViewingPoliza] = useState(null);
  const [polizaToAnular, setPolizaToAnular] = useState(null);
  
  const [authModalConfig, setAuthModalConfig] = useState({ open: false, type: '', item: null });

  // Estado del usuario actual para permisos
  const [currentUser, setCurrentUser] = useState({ rol: 'Invitado' });

  useEffect(() => {
      const storedUser = localStorage.getItem('userProfile');
      if (storedUser) {
          setCurrentUser(JSON.parse(storedUser));
      }
  }, []);

  // --- 1. LISTENER EN TIEMPO REAL (onSnapshot) ---
  useEffect(() => {
    if (!client?.id) return;

    setLoading(true);
    const polizasRef = collection(db, `public/data/clientes/${client.id}/polizas`);
    
    const unsubscribe = onSnapshot(polizasRef, async (snapshot) => {
        const rawData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Sincronización proactiva de estados basada en fechas (Data Healing)
        const synchronizedData = await Promise.all(rawData.map(async (p) => {
            const currentStatus = (p.estadoEmision || '').toUpperCase();

            // Escudo de estados que no deben re-calcularse
            const noCalcular = [
                'PENDIENTE DE EMISIÓN',
                'PENDIENTE DE INSPECCIÓN',
                'PROPUESTA INGRESADA',
                'ANULADA'
            ].includes(currentStatus);

            // ◄◄◄ CORREGIDO: Resolvemos el estado en tiempo real con soporte de inicio de vigencia nulo
            const realStatus = noCalcular 
                ? currentStatus 
                : getPolizaStatusInRealtime(p);

            if (realStatus !== currentStatus) {
                try {
                    const ref = doc(db, `public/data/clientes/${client.id}/polizas`, p.id);
                    // Actualizamos en la DB. El onSnapshot volverá a dispararse de forma síncrona.
                    await setDoc(ref, { estadoEmision: realStatus, ultimaModificacion: serverTimestamp() }, { merge: true });
                    return { ...p, estadoEmision: realStatus };
                } catch (e) { console.error("Error sync status:", e); }
            }
            return p;
        }));

        // Ordenamiento: Vigentes primero (basado en el estado real recalculado), luego por fin de vigencia descendente
        synchronizedData.sort((a, b) => {
            const estA = getPolizaStatusInRealtime(a);
            const estB = getPolizaStatusInRealtime(b);
            if (estA === 'VIGENTE' && estB !== 'VIGENTE') return -1;
            if (estA !== 'VIGENTE' && estB === 'VIGENTE') return 1;
            return new Date(b.finVigencia || 0) - new Date(a.finVigencia || 0);
        });

        setPolizas(synchronizedData);
        setLoading(false);
    }, (error) => {
        console.error("Error listener PolizasTab:", error);
        setLoading(false);
    });

    return () => unsubscribe();
  }, [client?.id]);

  // --- 2. PROTECCIÓN PARA CAUCIONES (Endoso 0) ---
  useEffect(() => {
    const checkCauciones = async () => {
      const cauciones = polizas.filter(p => (p.riesgo || '').toUpperCase().includes('CAUCI'));
      if (cauciones.length === 0) return;

      const newMap = { ...refactMap };
      let huboCambios = false;

      await Promise.all(cauciones.map(async (p) => {
        if (newMap[p.id] === undefined) {
          try {
            const ref = collection(db, `public/data/clientes/${client.id}/polizas/${p.id}/endosos`);
            const q = query(ref, where('numeroEndoso', '==', '0'), limit(1));
            const snap = await getDocs(q);
            newMap[p.id] = !snap.empty;
            huboCambios = true;
          } catch (e) { console.error(e); }
        }
      }));

      if (huboCambios) setRefactMap(newMap);
    };

    if (polizas.length > 0 && client?.id) checkCauciones();
  }, [polizas, client?.id, refactMap]);

  const handleNavigateToDetail = (poliza) => navigate(`/clientes/${client.id}/polizas/${poliza.id}`);
  const handleOpenEmission = (poliza = null) => { setEditingPoliza(poliza); setShowEmissionModal(true); };
  const handleViewDetail = (poliza) => { setViewingPoliza(poliza); setShowDetailModal(true); };
  
  const handleOpenAnulacion = (poliza) => {
      const isBlocked = (poliza.riesgo || '').toUpperCase().includes('CAUCI') && refactMap[poliza.id];
      if (isBlocked) return; 
      setPolizaToAnular(poliza);
      setShowAnulacionModal(true);
  };

  /**
   * handleAuthActionConfirm - Procesa Borrado y Rehabilitación delegando en los servicios dedicados
   */
  const handleAuthActionConfirm = async () => {
    const { type, item } = authModalConfig;
    if(!item || !client?.id) return;

    try {
        if (type === 'DELETE') {
            await deletePolizaFull(client.id, item.id);
            setPolizas(prev => prev.filter(p => p.id !== item.id));
        } 
        else if (type === 'REHABILITAR') {
            await rehabilitarPolizaFull(client.id, item.id);
        }
    } catch (error) {
        console.error(`Error en acción ${type}:`, error);
    } finally {
        setAuthModalConfig({ open: false, type: '', item: null });
    }
  };

  const handleSaveSuccess = (msg, newPoliza) => {
    setShowEmissionModal(false);
    setShowAnulacionModal(false);
  };

  // --- FILTRADO INTELIGENTE (Cruce Perfecto con getPolizaStatusInRealtime) ---
  const filteredPolizas = useMemo(() => {
    return polizas.filter(p => {
        const matchPoliza = filters.poliza ? (p.nPoliza || '').toLowerCase().includes(filters.poliza.toLowerCase()) : true;
        
        let matchPatente = true;
        if (filters.patente) {
            const searchVal = filters.patente.toLowerCase();
            const inOldField = (p.patente || '').toLowerCase().includes(searchVal);
            const inNewArray = Array.isArray(p.patentes) && p.patentes.some(pat => (pat || '').toLowerCase().includes(searchVal));
            matchPatente = inOldField || inNewArray;
        }

        // ◄◄◄ CORREGIDO: Filtramos en base al estado real recalculado para que coincida 100% con la Card
        const estadoRealCalculado = getPolizaStatusInRealtime(p);
        const matchEstado = filters.estado ? estadoRealCalculado === filters.estado.toUpperCase() : true;
        
        const matchMotivo = filters.motivo ? p.motivo === filters.motivo : true;
        const matchRiesgo = filters.riesgo ? p.riesgo === filters.riesgo : true;
        const matchCompania = filters.compania ? p.compania === filters.compania : true;

        return matchPoliza && matchPatente && matchEstado && matchMotivo && matchRiesgo && matchCompania;
    });
  }, [polizas, filters]);

  const uniqueOptions = (field) => [...new Set(polizas.map(p => p[field]).filter(Boolean))].sort();
  const optionsEstados = ['VIGENTE', 'PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA', 'VENCIDA', 'ANULADA', 'NO INICIADA'];

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6 animate-fade-in text-left font-sans">
      
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-left">
          <div className="flex items-center gap-4 flex-wrap text-left">
              <div className="flex items-center gap-2 text-gray-700 font-bold text-lg text-left">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  <span>{filteredPolizas.length} resultados</span>
              </div>
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-red-600 font-semibold text-sm hover:text-red-800 transition-colors text-left">
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            <svg className={`w-4 h-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
          </button>
      </div>

      {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-left animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 text-left">
                  <div className="space-y-1 text-left">
                      <label className="text-[10px] font-bold text-gray-400 uppercase text-left">1. Póliza N°</label>
                      <input type="text" placeholder="Buscar..." value={filters.poliza} onChange={e => handleFilterChange('poliza', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left" />
                  </div>
                  <div className="space-y-1 text-left">
                      <label className="text-xs font-bold text-gray-500 uppercase text-left">2. Patente</label>
                      <input type="text" placeholder="Buscar..." value={filters.patente} onChange={e => handleFilterChange('patente', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left" />
                  </div>
                  <div className="space-y-1 text-left">
                      <label className="text-xs font-bold text-gray-500 uppercase text-left">3. Estado</label>
                      <select value={filters.estado} onChange={e => handleFilterChange('estado', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left">
                          <option value="">Todos</option>
                          {optionsEstados.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                  </div>
                  <div className="space-y-1 text-left">
                      <label className="text-xs font-bold text-gray-500 uppercase text-left">4. Motivo</label>
                      <select value={filters.motivo} onChange={e => handleFilterChange('motivo', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left">
                          <option value="">Todos</option>
                          {uniqueOptions('motivo').map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                  </div>
                  <div className="space-y-1 text-left">
                      <label className="text-xs font-bold text-gray-500 uppercase text-left">5. Riesgo</label>
                      <select value={filters.riesgo} onChange={e => handleFilterChange('riesgo', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left">
                          <option value="">Todos</option>
                          {uniqueOptions('riesgo').map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                  </div>
                  <div className="space-y-1 text-left">
                      <label className="text-xs font-bold text-gray-500 uppercase text-left">Compañía</label>
                      <select value={filters.compania} onChange={e => handleFilterChange('compania', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left">
                          <option value="">Todas</option>
                          {uniqueOptions('compania').map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                  </div>
              </div>
          </div>
      )}

      {loading ? (
          <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-t-blue-600 border-gray-200 rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-2 text-sm font-bold uppercase tracking-widest">Sincronizando pólizas...</p>
          </div>
      ) : filteredPolizas.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-dashed border-gray-300">
              <p className="text-gray-900 font-medium italic">No se encontraron pólizas con los filtros seleccionados.</p>
          </div>
      ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left">
              {filteredPolizas.map(p => {
                  const r = (p.riesgo || '').toUpperCase();
                  const isPendingCheck = r.includes('CAUCI') && refactMap[p.id] === undefined;
                  const isBlockedByRefact = r.includes('CAUCI') && refactMap[p.id] === true;
                  const isAnularDisabled = isPendingCheck || isBlockedByRefact;

                  return (
                    <CardPoliza 
                        key={p.id} 
                        poliza={p}
                        onNavigate={() => handleNavigateToDetail(p)}
                        onView={() => handleViewDetail(p)} 
                        onEdit={() => handleOpenEmission(p)}
                        onAnular={() => handleOpenAnulacion(p)}
                        onDelete={() => setAuthModalConfig({ open: true, type: 'DELETE', item: p })}
                        onRehabilitar={() => setAuthModalConfig({ open: true, type: 'REHABILITAR', item: p })}
                        isAnularDisabled={isAnularDisabled}
                        isFullUser={currentUser.rol === 'Administrador' || currentUser.rol === 'Suscriptor' || currentUser.rol === 'Comercial'}
                    />
                  );
              })}
          </div>
      )}

      {/* Botón flotante para Nueva Emisión */}
      <button onClick={() => handleOpenEmission(null)} className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all hover:scale-105 z-40 flex items-center justify-center group active:scale-95">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out whitespace-nowrap ml-0 group-hover:ml-2 font-bold uppercase text-[10px] tracking-widest">Nueva Emisión</span>
      </button>

      {/* Modales */}
      <ModalEmision show={showEmissionModal} onClose={() => setShowEmissionModal(false)} onSaveSuccess={handleSaveSuccess} clientData={client} initialData={editingPoliza} />
      <ModalDetallePoliza show={showDetailModal} onClose={() => { setShowDetailModal(false); setViewingPoliza(null); }} poliza={viewingPoliza} />
      <ModalAnulacion show={showAnulacionModal} onClose={() => setShowAnulacionModal(false)} poliza={polizaToAnular} clientId={client.id} onSuccess={(msg, updatedPoliza) => handleSaveSuccess(msg, updatedPoliza)} />

      <AuthModal
        isOpen={authModalConfig.open}
        onClose={() => setAuthModalConfig({ open: false, type: '', item: null })}
        onConfirm={handleAuthActionConfirm} 
        title={authModalConfig.type === 'DELETE' ? 'Eliminar Póliza' : 'Rehabilitar Póliza'}
        message={authModalConfig.type === 'DELETE' 
            ? '¿Estás seguro? Esta acción borrará la póliza, toda su cobranza, endosos y siniestros permanentemente.'
            : '¿Deseas rehabilitar esta póliza? Su estado volverá a VIGENTE y se eliminarán físicamente los datos de anulación registrados.'
        }
        requireAuth={true}
      />
    </div>
  );
};

export default PolizasTab;